/* eslint-disable react/jsx-props-no-spreading */
import { ErrorOutline as ExclamationCircleOutlined } from "@mui/icons-material";
import { Tooltip } from "../../../Tooltip/Tooltip";
import classNames from "classnames";
import { ReactNode, useCallback, useContext, useMemo, useState } from "react";
import "react-quill-new/dist/quill.snow.css";
import { useCurrentUser } from "../../../../utils/hooks";
import { RCGReactQuillInput } from "../../../RCGReactQuillInput";
import {
  useDraftFieldValue,
  useFieldValue,
  useTemplateStoreFromContext,
} from "../../Context/hooks";
import { FieldStamp } from "../FieldAuditStamp";
import FieldChangeLog from "../FieldChangeLog";
import { useTemplateController } from "../FieldContext";
import FileInput from "../FieldFileInput";
import FieldStateSandwich from "../FieldStateSandwich.component";
import { getFormRulesFromMetadata } from "../utils";
import { InputFieldProps } from "./InputFieldBase";
import { useShallow } from "zustand/react/shallow";

const EMPTY_BOX = "<p><br></p>";

const TextAreaWrapper = ({
  children,
  templateKey,
  hideMultiStateView,
  className,
}: {
  children: ReactNode;
  templateKey: string;
  hideMultiStateView: boolean;
  className: string;
}) => {
  const currentUser = useCurrentUser();
  if (hideMultiStateView || !currentUser.flags.isSubscriptionUser) {
    return <div className={classNames(className)}>{children}</div>;
  }
  return (
    <FieldStateSandwich
      templateKey={templateKey}
      childClassName={classNames(className)}
    >
      {children}
    </FieldStateSandwich>
  );
};

export default function TextAreaFieldInput({
  metadata,
  templateOverride,
  templateKey,
  formLookupKey,
  NotApplicableWrapper,
}: InputFieldProps): JSX.Element {
  const displayName = templateOverride?.displayName || metadata.displayName;
  const questionText = templateOverride?.questionText || metadata.questionText;

  const formRules = getFormRulesFromMetadata(metadata.value);
  const { hideMultiStateView, hideTimeStamp } = metadata.value;

  const {
    field: { onChange, value, onBlur, ...inputProps },
    fieldState: { error },
  } = useTemplateController({
    name: formLookupKey,
    ...(formRules && { rules: formRules }),
  });

  const { hideImageUpload } = useTemplateStoreFromContext()(
    useShallow(state => ({
      hideImageUpload: state.hideImageUpload,
    }))
  );

  const { field } = useFieldValue(templateKey);
  const { field: draftField } = useDraftFieldValue(templateKey);
  let mostRecentField = field;
  if (
    (!field && draftField) ||
    (field && draftField && draftField.modifyDate > field?.modifyDate)
  ) {
    mostRecentField = draftField;
  }

  const handleChange = useCallback(
    (content: any, delta: any, source: any) => {
      if (source === "user") {
        onChange(content);
      }
    },
    [onChange]
  );

  const [focus, setFocus] = useState(false);
  const handleMouseEnter = useCallback(() => {
    setFocus(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setFocus(false);
  }, []);

  const errorStyle = useMemo(() => {
    return error
      ? {
          borderColor: "red",
          borderWidth: "2px",
          borderStyle: "solid",
        }
      : {};
  }, [error]);

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        data-cy="template__textarea"
        data-test-id="template__textarea"
        className={classNames("main-input__info")}
      >
        <div className="main-input__sublabel">{questionText}</div>
      </div>

      <TextAreaWrapper
        templateKey={templateKey}
        hideMultiStateView={!!hideMultiStateView}
        className="field-input-children-with-changelog"
      >
        {error && (
          <Tooltip title={error.message}>
            <ExclamationCircleOutlined style={{ color: "red" }} />
          </Tooltip>
        )}
        <NotApplicableWrapper templateKey={templateKey}>
          <>
            <div
              data-testid="quill_editor"
              data-cy={templateKey}
              className={"flex-column full-width"}
            >
              <FieldChangeLog
                templateKey={templateKey}
                shouldDisplayBadge={focus}
              />
              <RCGReactQuillInput
                toolbarId={templateKey.split(".")[1]}
                placeholder={displayName ?? ""}
                textValue={value ?? EMPTY_BOX}
                onChange={handleChange}
                style={errorStyle}
              />

              {!hideTimeStamp && <FieldStamp field={mostRecentField} />}
              {!hideImageUpload && (
                <FileInput
                  metadata={metadata}
                  templateKey={templateKey}
                  formLookupKey={`${templateKey}.documentIds`}
                />
              )}
            </div>
          </>
        </NotApplicableWrapper>
      </TextAreaWrapper>
    </div>
  );
}
